<script lang="ts">
	import { goto } from '$app/navigation'
	import { createEventDispatcher, onMount, onDestroy } from 'svelte'
	import { page } from '$app/stores'
	import { ChevronDownIcon } from '@rgossiaux/svelte-heroicons/outline'
	import { HomeIcon, CollectionIcon, NewspaperIcon, BellIcon } from '@rgossiaux/svelte-heroicons/solid'
	import { SearchCircleIcon } from '@rgossiaux/svelte-heroicons/solid'

	import { notifications, notification_bookmarks, newActivities } from '$lib/stores/notifications'

	import Menu from '$lib/components/Menu.svelte'
	import type MenuItem from '$lib/components/menu'
	import SearchInput from '$lib/components/SearchInput/Input.svelte'
	import ProfileComponent from '$lib/components/Header/Profile.svelte'
	import NotificationDot from '$lib/components/NotificationDot.svelte'
	import { trpc } from '$lib/trpc/client'
	import { user } from '$lib/stores/userData'
	import { searchType, searchString, searchFilter, searching } from '$lib/stores/searchInfo'
	import { browser } from '$app/environment'

	let interval: NodeJS.Timer
	let autoCompleteItems = ['']

	onMount(async () => {
		createNavMenu()

		autoCompleteItems = await trpc().stats.readTopSearchesByDay.query({ days: 7, count: 10 })

		if ($user && browser) {
			$newActivities = await getActivityCount()
			interval = setInterval(async () => {
				$newActivities = await getActivityCount()
			}, 60000 * 2)
		}
	})
	onDestroy(() => {
		if (interval) clearInterval(interval)
	})

	const getRoot = (path: string) => {
		const root = path.split('/')
		return '/' + root[1]
	}

	const getPageName = (pathname: string): string => {
		const root = getRoot(pathname)
		const page = links.find((link) => root === link.url)?.name

		if (page) {
			return page
		} else {
			let name = root.slice(1)
			if (name) {
				return name[0].toUpperCase() + name.slice(1)
			} else return ''
		}
	}

	$: journalName = import.meta.env.VITE_JOURNAL_NAME_PLURAL_CAPITALIZED

	let links: Array<{ name: string; url: string }> = [
		{ name: 'Home', url: '/' },
		{ name: 'My ' + import.meta.env.VITE_JOURNAL_NAME_PLURAL_CAPITALIZED, url: '/journals' },
		{ name: 'For You', url: '/foryou' },
		{ name: 'Search', url: '/search' },
		{ name: 'My Account', url: '/profile' },
		{ name: 'Bookmarks', url: '/saved' },
		{ name: 'Shared Journal', url: '/publicjournal' },
		{ name: 'Sign Up', url: '/signup' },
		{ name: 'Log In', url: '/login' }
	]

	let menuOpen = false
	let navMenu: MenuItem[] = []

	$: isHomePage = $page.route.id === '/'
	$: isForYouPage = $page.route.id?.startsWith('/foryou')
	$: isSearchPage = $page.route.id?.startsWith('/search')
	$: isJournalsPage = $page.route.id?.startsWith('/journals')

	const createNavMenu = () => {
		navMenu = [
			{
				type: 'link',
				name: 'Home',
				url: '/',
				isChecked: () => isHomePage ?? false
			},
			{
				type: 'link',
				name: 'For You',
				url: '/foryou',
				isChecked: () => isForYouPage ?? false
			},
			{
				type: 'link',
				name: 'Search',
				url: '/search',
				isChecked: () => isSearchPage ?? false
			},
			{
				type: 'link',
				name: `My ${journalName}`,
				url: '/journals',
				isChecked: () => isJournalsPage ?? false
			}
		]
	}

	$: pageName = getPageName($page.url.pathname)

	const onSubmitSearch = async (e: CustomEvent) => {
		if (browser) {
			$searching = true
			console.log('onSubmitSearch', e.detail)
			await goto(`/search?q=${e.detail}`)
			$searching = false
		}
	}
	const getActivityCount = async () => {
		if (!$user) return 0

		return await trpc().users.activity.readCount.query()
	}
	let navbar: HTMLElement
	const dispatch = createEventDispatcher()
	const forward = (e: CustomEvent) => dispatch(e.type, e.detail)
	let search = false
	// let searchInput: HTMLInputElement

	const onSearch = () => {
		search = true
		// searchInput.focus()
	}
	$: homePageClass = $page.route.id === '/' ? 'text-app-500' : 'text-gray-500 dark:text-gray-300'
	$: forYouClass = $page.route.id === '/foryou' ? 'text-app-500' : 'text-gray-500 dark:text-gray-300'
	$: searchClass = $page.route.id === '/search' ? 'text-app-500' : 'text-gray-500 dark:text-gray-300'

	$: journalsClass = $page.route.id === '/journals' ? 'text-app-500' : 'text-gray-500 dark:text-gray-300'
	$: updatesClass = $page.route.id === '/updates' ? 'text-app-500' : 'text-gray-500 dark:text-gray-300'
</script>

<div
	bind:this={navbar}
	class="sm:hidden fixed z-40 bottom-1 left-1 right-1 w-fit m-auto px-2 rounded-full border-t dark:border-gray-600 bg-gray-200/90 dark:bg-gray-800/80 backdrop-blur-sm drop-shadow-sm"
>
	<div class="flex justify-center items-center">
		<navbar class="cursor-pointer p-2 w-full items-center justify-center flex space-x-4">
			<a href="/" class="menu-button p-0">
				<HomeIcon
					class="
                	{homePageClass}
                	h-[38px] transition hover:text-app-500"
				/>
			</a>
			<a href="/foryou" class="menu-button p-0">
				<NewspaperIcon
					class="h-[38px] transition hover:text-app-500
                	{forYouClass}"
				/>
			</a>
			<a href="/search">
				<SearchCircleIcon
					on:click={() => onSearch()}
					class="h-[41px] menu-button p-0 hover:text-app-500
                    {searchClass}"
				/>
			</a>
			{#if $user}
				<a href="/journals" class="menu-button p-0">
					<CollectionIcon class="{journalsClass} h-[38px] hover:text-app-500" />
					<NotificationDot count={$notifications} />
				</a>
				<a href="/updates" class="menu-button p-0">
					<BellIcon
						class="{updatesClass}
							h-[38px]"
					/>
					<NotificationDot count={$newActivities} />
				</a>
			{/if}

			<div class="relative mt-1">
				<ProfileComponent on:signup={forward} on:login={forward} />
				<NotificationDot count={$notification_bookmarks} />
			</div>
		</navbar>
	</div>
</div>

<header
	class="fixed sm:flex top-0 w-full hidden pr-3 items-center justify-left sm:items-center sm:justify-center dark:bg-black/60 bg-white/60 backdrop-blur-md text-black dark:text-white z-40 filter"
>
	<div class="flex items-center w-full space-x-1 ml-1 pr-1">
		<div class="hidden sm:block">
			<Menu menuItems={navMenu} bind:open={menuOpen}>
				<svelte:fragment slot="menu-button">
					<nav class="relative flex cursor-pointer justify-left tracking-tight items-center">
						<div
							class="flex w-full space-x-1 items-center justify-left rounded-full p-1.5 px-2
                        {menuOpen ? 'text-white bg-black ' : 'text-app-500 dark:text-white'}"
						>
							{#if pageName}
								<div style:font-size={'1.2em'} class="font-bold whitespace-nowrap">
									{pageName}
								</div>
							{/if}
							<div class="">
								<ChevronDownIcon class="h-4" />
							</div>
						</div>
					</nav>
				</svelte:fragment>
			</Menu>
		</div>

		<div class="grow">
			<SearchInput
				bind:value={$searchString}
				bind:type={$searchType}
				bind:filter={$searchFilter}
				on:submit={onSubmitSearch}
				autoFocus={false}
				{autoCompleteItems}
			/>
		</div>

		<div class="relative mt-1">
			<ProfileComponent on:signup={forward} on:login={forward} />
			{#if $user}
				<NotificationDot count={$notification_bookmarks + $newActivities} />
			{/if}
		</div>
	</div>
</header>
